import { normalizeProduct, INSTALLATION_TYPES, PROPERTY_KEYS, installationError, municipalitySelectionError, patchInstallationProperties, readDraft, clearDraft } from '@apokto/recommendations';
import { CartLinesUpdateEvent } from '@shopify/events';

function storage() { try { return window.sessionStorage; } catch { return null; } }

let municipalityDataPromise = null;
async function loadMunicipalityData() {
  if (municipalityDataPromise) return municipalityDataPromise;
  const url = window.APOKTO_INSTALLATION_CONFIG?.municipalityDataUrl;
  if (!url) throw new Error('Archivio Comuni non configurato.');
  municipalityDataPromise = fetch(url, { headers: { Accept: 'application/json' } })
    .then((response) => {
      if (!response.ok) throw new Error('Archivio Comuni non disponibile.');
      return response.json();
    })
    .then((payload) => {
      if (!payload || !Array.isArray(payload.items) || !Array.isArray(payload.meta?.columns)) {
        throw new Error('Archivio Comuni non valido.');
      }
      const columns = Object.fromEntries(payload.meta.columns.map((name, index) => [name, index]));
      const required = ['istat','name','provinceCode','provinceName','region','cadastral','caps'];
      if (required.some((name) => !Number.isInteger(columns[name]))) throw new Error('Archivio Comuni incompleto.');
      const items = payload.items.map((row) => ({
        istatCode: String(row[columns.istat] || ''),
        name: String(row[columns.name] || ''),
        province: String(row[columns.provinceCode] || '').toUpperCase(),
        provinceName: String(row[columns.provinceName] || ''),
        region: String(row[columns.region] || ''),
        cadastral: String(row[columns.cadastral] || ''),
        caps: Array.isArray(row[columns.caps]) ? row[columns.caps].map(String) : [],
      }));
      if (items.length !== 7894) throw new Error('Archivio Comuni non aggiornato.');
      return { items, meta: payload.meta };
    });
  return municipalityDataPromise;
}

function normalizeSearch(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('it-IT').trim();
}

export class ApoktoInstallation extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.source = 'pdp';
    this.family = this.dataset.family || '';
    this.context = this.dataset.context || 'quiz';
    const data = this.querySelector('[data-install-product]');
    if (data) {
      try { this.product = normalizeProduct(JSON.parse(data.textContent)); } catch { this.hidden = true; return; }
      if (!this.product.eligible) { this.hidden = true; return; }
      this.family = this.product.installationFamily;
    }
    this.setFamily(this.family);
    this.hidden = false;

    this.field('municipality')?.addEventListener('input', () => this.onMunicipalityInput(), { signal });
    this.field('municipality')?.addEventListener('keydown', (event) => this.onMunicipalityKeydown(event), { signal });
    this.querySelector('[data-install-municipality-list]')?.addEventListener('mousedown', (event) => {
      const option = event.target.closest('[data-municipality-index]');
      if (!option) return;
      event.preventDefault();
      this.selectMunicipality(Number(option.dataset.municipalityIndex));
    }, { signal });

    this.addEventListener('change', (event) => {
      if (event.target !== this.field('municipality')) this.sync();
    }, { signal });
    this.addEventListener('input', (event) => {
      event.target?.removeAttribute('aria-invalid');
      if (this.error) this.error.textContent = '';
    }, { signal });
    this.querySelector('[data-install-optout]')?.addEventListener('click', () => this.reset(), { signal });

    if (this.context === 'pdp') {
      this.form = this.closest('form');
      this.form?.addEventListener('formdata', (event) => this.prepare(event.formData), { signal });
      this.form?.addEventListener('submit', (event) => {
        if (!this.validate()) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, { capture: true, signal });
      const draft = readDraft(storage(), this.product);
      if (draft) this.setValue(draft);
    }
    this.sync();
  }

  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  field(name) { return this.querySelector('[data-install-field="' + name + '"]'); }
  get error() { return this.querySelector('[data-install-error]'); }
  get listbox() { return this.querySelector('[data-install-municipality-list]'); }

  get value() {
    return {
      requested: Boolean(this.field('requested')?.checked),
      family: this.family,
      postalCode: this.field('postalCode')?.value || '',
      municipality: this.field('municipality')?.value.trim() || '',
      province: this.field('province')?.value.trim().toUpperCase() || '',
      address: this.field('address')?.value.trim() || '',
      type: this.field('type')?.value || '',
      source: this.source,
      istatCode: this.querySelector('[data-install-istat-code]')?.value || '',
      region: this.querySelector('[data-install-region]')?.value || '',
      cadastral: this.querySelector('[data-install-cadastral]')?.value || '',
      locationSource: 'istat',
    };
  }

  setFamily(family) {
    const changed = this.family !== family;
    this.family = family;
    const select = this.field('type');
    if (!select) return;
    const old = changed ? '' : select.value;
    select.replaceChildren(new Option('Seleziona la tipologia', ''));
    for (const [id, label] of INSTALLATION_TYPES[family] || []) select.add(new Option(label, id));
    select.value = old;
    const hiddenFamily = this.querySelector('[data-install-family]');
    if (hiddenFamily) hiddenFamily.value = family;
    if (changed) this.reset();
  }

  setValue(value) {
    this.setFamily(value.family);
    this.field('requested').checked = Boolean(value.requested);
    this.field('address').value = value.address || '';
    this.field('municipality').value = value.municipality || '';
    this.field('province').value = (value.province || '').toUpperCase();
    this.field('type').value = value.type || '';
    this.setHiddenLocation(value);
    this.setCapOptions(Array.isArray(value.caps) ? value.caps : [value.postalCode].filter(Boolean), value.postalCode || '');
    this.source = value.source === 'quiz' ? 'quiz' : 'pdp';
    this.draftExpiresAt = value.expiresAt || null;
    this.pendingMunicipalityRestore = { ...value };
    this.sync();
  }

  setHiddenLocation(value = {}) {
    const map = [
      ['[data-install-istat-code]', value.istatCode || ''],
      ['[data-install-region]', value.region || ''],
      ['[data-install-cadastral]', value.cadastral || ''],
      ['[data-install-location-source]', 'istat'],
    ];
    for (const [selector, val] of map) {
      const node = this.querySelector(selector);
      if (node) node.value = val;
    }
  }

  clearMunicipalitySelection({ keepText = true } = {}) {
    if (!keepText && this.field('municipality')) this.field('municipality').value = '';
    if (this.field('province')) this.field('province').value = '';
    this.setHiddenLocation({});
    this.setCapOptions([], '');
    this.selectedMunicipality = null;
  }

  setCapOptions(caps, selected = '') {
    const select = this.field('postalCode');
    if (!select) return;
    const validCaps = [...new Set((caps || []).filter((cap) => /^\d{5}$/.test(String(cap))).map(String))];
    select.replaceChildren();
    if (!validCaps.length) {
      select.add(new Option('Seleziona prima il Comune', ''));
      select.disabled = true;
      return;
    }
    if (validCaps.length > 1) select.add(new Option('Seleziona il CAP', ''));
    for (const cap of validCaps) select.add(new Option(cap, cap));
    select.disabled = false;
    if (selected && validCaps.includes(String(selected))) select.value = String(selected);
    else if (validCaps.length === 1) select.value = validCaps[0];
    else select.value = '';
  }

  async ensureMunicipalities() {
    try {
      if (!this.municipalities) {
        const data = await loadMunicipalityData();
        this.municipalities = data.items;
      }
      if (this.pendingMunicipalityRestore) {
        this.restoreMunicipalitySelection(this.pendingMunicipalityRestore);
        this.pendingMunicipalityRestore = null;
      }
      return this.municipalities;
    } catch (error) {
      if (this.error) this.error.textContent = error?.message || 'Impossibile caricare l’elenco Comuni.';
      return [];
    }
  }

  restoreMunicipalitySelection(value) {
    if (!this.municipalities?.length || !value?.municipality) return;
    const targetCode = String(value.istatCode || '');
    const targetName = normalizeSearch(value.municipality);
    const targetProvince = String(value.province || '').toUpperCase();
    const match = this.municipalities.find((item) =>
      (targetCode && item.istatCode === targetCode) ||
      (!targetCode && normalizeSearch(item.name) === targetName && item.province === targetProvince));
    if (!match) {
      this.clearMunicipalitySelection({ keepText: true });
      return;
    }
    this.applyMunicipality(match, value.postalCode || '');
  }

  async onMunicipalityInput() {
    const input = this.field('municipality');
    if (!input) return;
    this.clearMunicipalitySelection({ keepText: true });
    const query = normalizeSearch(input.value);
    if (query.length < 2) { this.closeMunicipalityList(); return; }
    const municipalities = await this.ensureMunicipalities();
    const starts = [];
    const contains = [];
    for (let index = 0; index < municipalities.length; index++) {
      const item = municipalities[index];
      const name = normalizeSearch(item.name);
      if (name.startsWith(query)) starts.push(index);
      else if (name.includes(query)) contains.push(index);
      if (starts.length >= 12) break;
    }
    const matches = starts.length >= 12 ? starts.slice(0, 12) : [...starts, ...contains].slice(0, 12);
    this.renderMunicipalityList(matches);
  }

  renderMunicipalityList(indices) {
    const list = this.listbox;
    const input = this.field('municipality');
    if (!list || !input) return;
    list.replaceChildren();
    this.activeMunicipalityOption = -1;
    if (!indices.length) {
      const empty = document.createElement('li');
      empty.className = 'apk-installation__municipality-empty';
      empty.textContent = 'Nessun Comune trovato';
      empty.setAttribute('role', 'presentation');
      list.append(empty);
    } else {
      for (const index of indices) {
        const item = this.municipalities[index];
        const option = document.createElement('li');
        option.role = 'option';
        option.tabIndex = -1;
        option.dataset.municipalityIndex = String(index);
        option.id = input.id + '-option-' + index;
        option.textContent = item.name + ' (' + item.province + ') — ' + item.provinceName;
        list.append(option);
      }
    }
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  closeMunicipalityList() {
    const input = this.field('municipality');
    if (this.listbox) this.listbox.hidden = true;
    input?.setAttribute('aria-expanded', 'false');
    input?.removeAttribute('aria-activedescendant');
    this.activeMunicipalityOption = -1;
  }

  onMunicipalityKeydown(event) {
    const options = [...(this.listbox?.querySelectorAll('[data-municipality-index]') || [])];
    if (event.key === 'Escape') { this.closeMunicipalityList(); return; }
    if (!options.length || this.listbox?.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const next = Math.max(0, Math.min(options.length - 1, (this.activeMunicipalityOption ?? -1) + delta));
      this.activeMunicipalityOption = next;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
      this.field('municipality')?.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.activeMunicipalityOption >= 0) {
      event.preventDefault();
      this.selectMunicipality(Number(options[this.activeMunicipalityOption].dataset.municipalityIndex));
    }
  }

  selectMunicipality(index) {
    const item = this.municipalities?.[index];
    if (!item) return;
    this.applyMunicipality(item);
    this.closeMunicipalityList();
    this.field('postalCode')?.focus();
  }

  applyMunicipality(item, preferredCap = '') {
    this.selectedMunicipality = item;
    this.field('municipality').value = item.name;
    this.field('province').value = item.province;
    this.setHiddenLocation({ istatCode: item.istatCode, region: item.region, cadastral: item.cadastral });
    this.setCapOptions(item.caps, preferredCap);
    this.field('municipality')?.removeAttribute('aria-invalid');
    if (this.error) this.error.textContent = '';
  }

  sync() {
    const requested = this.value.requested;
    this.dataset.requested = String(requested);
    this.field('requested')?.setAttribute('aria-expanded', String(requested));
    const fields = this.querySelector('[data-install-fields]');
    if (fields) { fields.hidden = !requested; fields.disabled = !requested; }
    const optout = this.querySelector('[data-install-optout-box]') || this.querySelector('[data-install-optout]');
    if (optout) optout.hidden = this.context === 'cart' || !requested;
    const sourceField = this.querySelector('[data-install-source]');
    if (sourceField) sourceField.value = this.source;
    if (requested) void this.ensureMunicipalities();
    if (!requested) {
      this.closeMunicipalityList();
      if (this.error) this.error.textContent = '';
      if (this.context !== 'cart') clearDraft(storage());
    }
    if (this.context === 'pdp') {
      const checkout = this.closest('product-form-component')?.querySelector('.accelerated-checkout-block');
      if (checkout) {
        if (requested && !this.checkoutState) {
          this.checkoutState = { hidden: checkout.hidden, inert: checkout.inert };
          checkout.hidden = true;
          checkout.inert = true;
        } else if (!requested && this.checkoutState) {
          checkout.hidden = this.checkoutState.hidden;
          checkout.inert = this.checkoutState.inert;
          this.checkoutState = null;
        }
      }
    }
  }

  validate() {
    if (this.value.requested && this.draftExpiresAt && Date.now() >= this.draftExpiresAt) {
      this.reset();
      this.error.textContent = 'Il draft installazione è scaduto. Attiva di nuovo la richiesta e reinserisci i dati.';
      this.field('requested').focus();
      return false;
    }
    const baseError = installationError(this.value);
    const error = baseError || municipalitySelectionError(this.value, this.selectedMunicipality);
    if (!error) { if (this.error) this.error.textContent = ''; return true; }
    this.error.textContent = error.message;
    const input = this.field(error.field);
    input?.setAttribute('aria-invalid', 'true');
    input?.focus();
    return false;
  }

  prepare(formData) {
    for (const key of Object.values(PROPERTY_KEYS)) formData.delete('properties[' + key + ']');
    if (this.value.requested && !installationError(this.value)) {
      const properties = patchInstallationProperties({}, this.value);
      for (const [key, value] of Object.entries(properties)) formData.set('properties[' + key + ']', value);
    }
  }

  reset() {
    for (const name of ['municipality', 'province', 'address', 'type']) {
      const input = this.field(name);
      if (input) input.value = '';
    }
    this.setCapOptions([], '');
    this.setHiddenLocation({});
    if (this.field('requested')) this.field('requested').checked = false;
    this.source = 'pdp';
    this.draftExpiresAt = null;
    this.selectedMunicipality = null;
    this.pendingMunicipalityRestore = null;
    clearDraft(storage());
    this.sync();
  }

  added() { this.reset(); }
}
if (!customElements.get('apokto-installation')) customElements.define('apokto-installation', ApoktoInstallation);

function normalizedProperties(properties) {
  return Object.entries(properties || {}).filter(([, value]) => value != null)
    .map(([key, value]) => [String(key), String(value)])
    .sort(([a], [b]) => a.localeCompare(b));
}
function sameProperties(left, right) {
  return JSON.stringify(normalizedProperties(left)) === JSON.stringify(normalizedProperties(right));
}

let mutationPending = false;
export class ApoktoCartInstallation extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-install-action]');
      if (!button || !this.contains(button)) return;
      const action = button.dataset.installAction;
      if (action === 'add' || action === 'edit') this.openEditor(action);
      else if (action === 'cancel') this.closeEditor();
      else if (action === 'save' || action === 'remove') void this.update(action);
    }, { signal });
    queueMicrotask(() => { if (this.isConnected) this.initialize(); });
  }

  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  get editor() { return this.querySelector('apokto-installation'); }
  get panel() { return this.querySelector('[data-install-editor]'); }
  get message() { return this.querySelector('[data-install-message]'); }

  readJson(selector) {
    try { return JSON.parse(this.querySelector(selector)?.textContent || ''); }
    catch { return null; }
  }

  initialize() {
    const rawValue = this.readJson('[data-install-value]');
    const rawProduct = this.readJson('[data-install-product]');
    if (!rawValue || !rawProduct || !this.editor) { this.hidden = true; return; }
    try { this.product = normalizeProduct(rawProduct); } catch { this.hidden = true; return; }
    this.persistedValue = {
      requested: Boolean(rawValue.requested),
      family: rawValue.family || this.product.installationFamily || '',
      postalCode: rawValue.postalCode || '',
      municipality: rawValue.municipality || '',
      province: rawValue.province || '',
      address: rawValue.address || '',
      type: rawValue.type || '',
      source: rawValue.source === 'quiz' ? 'quiz' : 'pdp',
      istatCode: rawValue.istatCode || '',
      region: rawValue.region || '',
      cadastral: rawValue.cadastral || '',
      locationSource: 'istat',
    };
    if (!this.persistedValue.requested && !this.product.eligible) { this.hidden = true; return; }
    this.hidden = false;
    this.renderState();
    this.initializeEditor(this.persistedValue);
  }

  renderState() {
    const requested = Boolean(this.persistedValue?.requested);
    const empty = this.querySelector('[data-install-empty]');
    const summary = this.querySelector('[data-install-summary]');
    if (empty) empty.hidden = requested;
    if (summary) summary.hidden = !requested;
  }

  initializeEditor(value) {
    this.editor.setValue({
      requested: Boolean(value?.requested),
      family: value?.family || this.product?.installationFamily || '',
      postalCode: value?.postalCode || '',
      municipality: value?.municipality || '',
      province: value?.province || '',
      address: value?.address || '',
      type: value?.type || '',
      source: value?.source === 'quiz' ? 'quiz' : 'pdp',
      istatCode: value?.istatCode || '',
      region: value?.region || '',
      cadastral: value?.cadastral || '',
      locationSource: 'istat',
    });
    this.editor.querySelector('.apk-installation__toggle').hidden = true;
    this.editor.querySelector('[data-install-optout-box]')?.setAttribute('hidden', '');
    this.editor.querySelector('[data-install-optout]')?.setAttribute('hidden', '');
  }

  openEditor(action) {
    if (action === 'add' && !this.product?.eligible) return;
    const value = action === 'add'
      ? { requested:true, family:this.product.installationFamily, postalCode:'', municipality:'', province:'', address:'', type:'', source:'pdp', istatCode:'', region:'', cadastral:'', locationSource:'istat' }
      : { ...this.persistedValue, requested:true };
    this.initializeEditor(value);
    this.panel.hidden = false;
    this.panel.disabled = false;
    this.querySelectorAll('[data-install-action="add"],[data-install-action="edit"]').forEach((button) =>
      button.setAttribute('aria-expanded', String(button.dataset.installAction === action)));
    this.editor.field('address')?.focus();
  }

  closeEditor() {
    this.panel.hidden = true;
    this.panel.disabled = true;
    this.initializeEditor(this.persistedValue);
    this.querySelectorAll('[data-install-action="add"],[data-install-action="edit"]').forEach((button) => button.setAttribute('aria-expanded','false'));
    const action = this.persistedValue?.requested ? 'edit' : 'add';
    this.querySelector('[data-install-action="' + action + '"]')?.focus();
  }

  async update(action) {
    if (mutationPending) return;
    if (action === 'save' && !this.editor.validate()) return;
    mutationPending = true;
    const submittedValue = action === 'remove' ? { requested:false } : { ...this.editor.value };
    const component = this.closest('cart-items-component');
    const buttons = [...this.querySelectorAll('button[data-install-action]')];
    const buttonStates = buttons.map((button) => [button, button.disabled]);
    let newKey = this.dataset.key;
    let changed = false;
    try {
      component?.setAttribute('aria-busy','true');
      for (const [button] of buttonStates) button.disabled = true;
      if (this.panel) this.panel.disabled = true;
      if (this.message) this.message.textContent = 'Aggiornamento in corso…';

      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { headers:{ Accept:'application/json' } });
      if (!cartResponse.ok) throw new Error('Impossibile leggere il carrello. Riprova.');
      const cart = await cartResponse.json();
      const lineIndex = cart.items.findIndex((item) => item.key === this.dataset.key);
      if (lineIndex < 0) throw new Error('La riga è cambiata. Aggiorna il carrello e riprova.');
      const line = cart.items[lineIndex];
      const properties = patchInstallationProperties(line.properties || {}, submittedValue);
      const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')]
        .map((node) => node.dataset.sectionId).filter(Boolean))].slice(0,5);

      const response = await fetch(Theme.routes.cart_change_url, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Accept:'application/json' },
        body:JSON.stringify({ id:line.key, quantity:line.quantity, properties, sections:sectionIds, sections_url:location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.errors || result.status) throw new Error('Impossibile aggiornare i dati. Riprova.');
      changed = true;

      const updatedLine = result.items?.find((item) =>
        String(item.variant_id) === String(line.variant_id) && sameProperties(item.properties || {}, properties)) || result.items?.[lineIndex];
      newKey = updatedLine?.key || line.key;

      const eventPromise = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action:'update',
        context:'cart',
        lines:[{ id:newKey, quantity:updatedLine?.quantity ?? line.quantity }],
        promise:eventPromise.promise
      }));
      eventPromise.resolve({
        cart:CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail:{ sections:result.sections, items:result.items, itemCount:result.item_count, source:'apokto-installation', didError:false }
      });

      if (!sectionIds.length || sectionIds.some((id) => !result.sections?.[id])) {
        location.assign(Theme.routes.cart_url);
        return;
      }

      const statusText = action === 'remove' ? 'Richiesta installazione rimossa.' : 'Dati installazione aggiornati.';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const nextComponent = [...document.querySelectorAll('apokto-cart-installation')].find((node) => node.dataset.key === newKey);
        const nextMessage = nextComponent?.querySelector('[data-install-message]');
        if (nextMessage) nextMessage.textContent = statusText;
        const target = nextComponent?.querySelector('[data-install-action="' + (action === 'remove' ? 'add' : 'edit') + '"]');
        target?.focus();
      }));
    } catch (error) {
      if (this.message) {
        this.message.textContent = changed
          ? 'Dati salvati. Ricarica il carrello per aggiornare il riepilogo.'
          : (error?.message || 'Impossibile aggiornare i dati. Riprova.');
        this.message.setAttribute('tabindex','-1');
        this.message.focus();
      }
    } finally {
      component?.removeAttribute('aria-busy');
      if (this.isConnected) {
        for (const [button, wasDisabled] of buttonStates) button.disabled = wasDisabled;
        if (this.panel) this.panel.disabled = this.panel.hidden;
      }
      mutationPending = false;
    }
  }
}
if (!customElements.get('apokto-cart-installation')) customElements.define('apokto-cart-installation', ApoktoCartInstallation);
